import React from 'react';

import TextField from '@material-ui/core/TextField';

import Checker from './Checker';
import InputLabel from './InputLabel';
import { hasValue } from '../../utils/helpers';
import ThreeWaySwitch from './ThreeWaySwitch';
import IModel from '../../models/model';
import useFieldValidation from 'hooks/validation/useFieldValidation';
import { TFieldValidatorFn, TValidationMode } from 'hooks/validation/types';
import { InputBaseComponentProps } from '@material-ui/core';
import { useMissingFieldError } from 'components/product/ProductUpdateForm/validation-context/ProductValidationContext';
import useDebouncedStateString from 'hooks/useDebouncedState';
import AiFieldVerdict from 'components/product/ProductUpdateForm/AiFieldVerdict';

export interface ILabelOptions {
  heading?: string;
  text?: string;
  link?: string;
  required?: boolean;
}

export interface IInputTextFieldProps<T extends IModel> {
  readOnly?: boolean;
  validatorMode?: TValidationMode;
  validator?: TFieldValidatorFn;
  inputName: keyof T;
  value?: any; // TODO
  label?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent) => void;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  multiline?: boolean;
  multilineRows?: number;
  multilineMax?: number;
  style?: React.CSSProperties;
  withLabel?: boolean;
  dense?: boolean;
  labelOptions?: ILabelOptions;
  aiButton?: React.ReactNode;
  showRed?: boolean;
  noFullWidth?: boolean;
  maxLength?: number;
  approve?: boolean;
  state?: T;
  setState?: (value: T) => void;
  errorText?: string;
  hideErrorText?: boolean;
  helpText?: string | React.ReactNode;
  placeholder?: string;
  forceRed?: boolean;
  ignoreTabs?: boolean;
  shrink?: boolean;
  className?: string;
  inputClassName?: string;
  size?: 'small' | 'medium';
  endAdornment?: React.ReactNode;
  spellCheck?: boolean;
  inputProps?: InputBaseComponentProps;
  hideComponentLabel?: boolean;
  // The field shows the failed AI review verdict itself, e.g. after its own messages or below its row.
  hideAiVerdict?: boolean;
}

const InputTextField = <T extends IModel>({
  readOnly,
  hideAiVerdict,
  validatorMode,
  validator,
  inputName,
  value,
  label,
  onChange,
  onFocus,
  onBlur,
  onKeyPress,
  disabled: disabledProp,
  required,
  autoFocus,
  multiline,
  multilineRows = 2,
  multilineMax = 3,
  style,
  withLabel,
  dense,
  labelOptions,
  aiButton,
  showRed,
  noFullWidth,
  maxLength,
  approve,
  state,
  setState,
  errorText,
  hideErrorText,
  helpText,
  placeholder,
  forceRed,
  ignoreTabs,
  shrink,
  className,
  inputClassName,
  size = 'medium',
  endAdornment,
  spellCheck,
  inputProps,
  hideComponentLabel = false,
}: IInputTextFieldProps<T>) => {
  const disabled = readOnly || disabledProp;
  const validation = useFieldValidation(inputName as string, {
    mode: validatorMode,
    required,
    value,
    validator,
    onBlur,
  });

  let error = false;
  if (required && showRed) {
    error = !hasValue(value);
  }
  if (forceRed) {
    error = true;
  }

  let errorMessage = errorText;

  // FC-1950: retailer-required field that is still missing (no-op outside the product form).
  const [touched, setTouched] = React.useState(false);
  const missingFieldError = useMissingFieldError(inputName?.toString(), touched);
  if (missingFieldError) {
    error = true;
    errorMessage = errorMessage || missingFieldError;
  }

  // For now, check [validation.mode] condition to make [useFieldValidation] stop working with old field
  if (validation.mode) {
    if (validation.error) {
      error = !!validation.error;
      if (!errorText) {
        errorMessage = validation.error;
      }
    }
  }
  let Check;

  if (approve && state?.approve?.find(x => x.field === inputName)) {
    // const value = state?.approve?.find((x) => x.field === inputName)?.state;
    Check = (
      <ThreeWaySwitch
        readOnly={readOnly}
        // value={value}
        name={inputName}
        state={state}
        setState={setState}
      />
    );
  } else {
    Check = !error ? (
      value || value === 0 ? (
        <Checker showRed={showRed} error={false} />
      ) : (
        <></>
      )
    ) : (
      <Checker showRed={showRed} error={true} />
    );
  }
  return (
    <>
      {withLabel && (
        <>
          {aiButton ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
              <InputLabel {...labelOptions} />
              {aiButton}
            </div>
          ) : (
            <InputLabel {...labelOptions} />
          )}
        </>
      )}
      <TextField
        style={{ marginTop: '5px', ...style }}
        placeholder={placeholder}
        error={error}
        fullWidth={!noFullWidth}
        multiline={multiline}
        rows={multilineRows}
        rowsMax={multilineMax}
        id={inputName.toString()}
        label={hideComponentLabel ? undefined : label}
        margin={dense ? 'dense' : 'normal'}
        name={inputName.toString()}
        disabled={disabled}
        required={required}
        autoFocus={!disabled && autoFocus}
        value={value}
        variant="outlined"
        onChange={onChange}
        onFocus={onFocus}
        onBlur={event => {
          setTouched(true);
          validation.onBlur?.(event as React.FocusEvent<HTMLInputElement>);
        }}
        onKeyPress={onKeyPress}
        className={className}
        inputProps={{
          maxLength,
          spellCheck,
          'aria-invalid': error,
          'aria-describedby': error ? errorMessage : undefined,
          ...inputProps,
        }}
        InputProps={{
          endAdornment: endAdornment || Check,
          classes: {
            input: inputClassName,
          },
        }}
        InputLabelProps={{
          shrink: shrink,
        }}
        size={size}
        helperText={(error && !hideErrorText && errorMessage) || helpText}
      />
      {/* Superusers see a failed AI review verdict under the field. */}
      {!hideAiVerdict && <AiFieldVerdict field={inputName?.toString()} />}
      {/* {approve && (
                <ThreeWaySwitch
                    value={null}
                />
            )} */}
    </>
  );
};

interface IInputTextFieldDebouncedProps<T extends IModel> extends Omit<IInputTextFieldProps<T>, 'setState'> {
  setState: React.Dispatch<React.SetStateAction<T>>;
}

export const InputTextFieldDebounced = <T extends IModel>({
  inputName,
  state,
  setState,
  ...props
}: IInputTextFieldDebouncedProps<T>) => {
  const [value, onValueChange] = useDebouncedStateString(String(state?.[inputName] ?? ''), setState, inputName);

  return (
    <InputTextField
      {...props}
      inputName={inputName}
      state={state}
      setState={setState}
      value={value}
      onChange={event => onValueChange(event.target.value)}
    />
  );
};

export default InputTextField;
